import React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createStyles, palette } from "../../../styles/theme";
import {
  SpecsImageUploadAdornment,
  SpecsImageUrlPreviewButton,
} from "../SpecsEditorFields/index";
import {
  uploadSpecsImageFile,
  type SpecsImageUploadContext,
} from "../specsImageUpload";

const styles = createStyles({
  compactInput: {
    minWidth: 88,
    height: 45,
    "& .MuiInputBase-root": {
      fontSize: 12,
      minHeight: 45,
      height: 45,
      px: 0.75,
      boxSizing: "border-box",
    },
    "& .MuiInputBase-input": {
      py: 0,
      height: "100%",
      boxSizing: "border-box",
    },
  },
  checkboxBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 45,
    height: 45,
    pt: 0,
  },
  checkbox: {
    p: 0.25,
  },
  colorInput: {
    width: 28,
    height: 28,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },
  emptyColorSwatch: {
    width: 28,
    height: 28,
    border: `1px dashed ${palette.grey.lightGrey}`,
    borderRadius: 0.5,
    background:
      "linear-gradient(135deg, transparent 0 45%, #c0cad6 45% 55%, transparent 55% 100%)",
    flexShrink: 0,
  },
  jsonEditor: {
    width: "100%",
    "& .MuiInputBase-root": {
      fontFamily: "monospace",
      fontSize: 12,
    },
  },
  displayCellButton: {
    minHeight: 45,
    height: 45,
    minWidth: 88,
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      bgcolor: palette.grey.lightBackground,
    },
  },
  displayCellText: {
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  mutedText: {
    color: "text.secondary",
  },
  colorDisplaySwatch: {
    width: 16,
    height: 16,
    borderRadius: 0.5,
    border: "1px solid rgba(0,0,0,0.15)",
    flexShrink: 0,
  },
});

export function CompactTextCell({
  value,
  onChange,
  minWidth,
  maxWidth,
  uploadContext,
  addLeftPreview = false,
}: {
  value: string;
  onChange: (nextValue: string) => void;
  minWidth?: number;
  maxWidth?: number;
  uploadContext?: SpecsImageUploadContext;
  addLeftPreview?: boolean;
}) {
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState("");

  const handleUpload = React.useCallback(
    async (file: File) => {
      setUploading(true);
      setUploadError("");

      try {
        const nextUrl = await uploadSpecsImageFile(file, uploadContext);
        onChange(nextUrl);
      } catch (error) {
        setUploadError(
          error instanceof Error ? error.message : "Failed to upload image"
        );
      } finally {
        setUploading(false);
      }
    },
    [onChange, uploadContext]
  );

  const fieldMinWidth =
    addLeftPreview && minWidth ? Math.max(minWidth - 32, 56) : minWidth;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          ...(maxWidth ? { maxWidth, width: "100%" } : null),
        }}
      >
        {addLeftPreview ? <SpecsImageUrlPreviewButton src={value} /> : null}
        <TextField
          value={value}
          variant="standard"
          size="small"
          InputProps={{
            disableUnderline: true,
            ...(uploadContext
              ? {
                  endAdornment: (
                    <SpecsImageUploadAdornment
                      uploading={uploading}
                      onUpload={handleUpload}
                    />
                  ),
                }
              : null),
          }}
          onChange={(event) => onChange(event.target.value)}
          sx={{
            ...styles.compactInput,
            flex: 1,
            minWidth: 0,
            ...(fieldMinWidth ? { minWidth: fieldMinWidth } : null),
          }}
        />
      </Box>
      {uploadError ? (
        <Alert severity="error" sx={{ mt: 0.5, py: 0, fontSize: 11 }}>
          {uploadError}
        </Alert>
      ) : null}
    </Box>
  );
}

export function CompactDisplayTextCell({
  value,
  minWidth,
  maxWidth,
  onClick,
  addLeftPreview = false,
}: {
  value: string;
  minWidth?: number;
  maxWidth?: number;
  onClick?: () => void;
  addLeftPreview?: boolean;
}) {
  const displayMinWidth =
    addLeftPreview && minWidth ? Math.max(minWidth - 32, 56) : minWidth;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        ...(maxWidth ? { maxWidth, width: "100%" } : null),
      }}
    >
      {addLeftPreview ? <SpecsImageUrlPreviewButton src={value} /> : null}
      <Box
        onClick={onClick}
        sx={{
          ...styles.displayCellButton,
          flex: 1,
          minWidth: 0,
          ...(displayMinWidth ? { minWidth: displayMinWidth } : null),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            ...styles.displayCellText,
            ...(value ? null : styles.mutedText),
          }}
        >
          {value || ""}
        </Typography>
      </Box>
    </Box>
  );
}

export function CompactCheckboxCell({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (nextValue: boolean) => void;
}) {
  return (
    <Box sx={styles.checkboxBox}>
      <Checkbox
        size="small"
        checked={checked}
        onChange={(_, nextValue) => onChange(nextValue)}
        sx={styles.checkbox}
      />
    </Box>
  );
}

function isHexColor(value: string) {
  return /^#[0-9a-fA-F]{6}$/.test(value.trim());
}

export function CompactColorCell({
  value,
  onChange,
}: {
  value: string;
  onChange: (nextValue: string) => void;
}) {
  const normalizedValue = value.trim();
  const hasColorPreview = isHexColor(normalizedValue);

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ height: 45 }}
    >
      {hasColorPreview ? (
        <input
          type="color"
          value={normalizedValue}
          onChange={(event) => onChange(event.target.value)}
          style={styles.colorInput as React.CSSProperties}
        />
      ) : (
        <Box sx={styles.emptyColorSwatch} title="No color value" />
      )}
      <CompactTextCell value={value} onChange={onChange} minWidth={96} />
    </Stack>
  );
}

export function JsonEditor({
  value,
  onChange,
  minRows = 4,
}: {
  value: unknown;
  onChange: (nextValue: unknown) => void;
  minRows?: number;
}) {
  const [inputValue, setInputValue] = React.useState(() =>
    JSON.stringify(value ?? {}, null, 2)
  );
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setInputValue(JSON.stringify(value ?? {}, null, 2));
    setError(null);
  }, [value]);

  return (
    <TextField
      value={inputValue}
      multiline
      minRows={minRows}
      size="small"
      variant="standard"
      error={Boolean(error)}
      helperText={error}
      InputProps={{ disableUnderline: true }}
      onChange={(event) => {
        const nextInputValue = event.target.value;
        setInputValue(nextInputValue);

        if (!nextInputValue.trim()) {
          setError(null);
          onChange({});
          return;
        }

        try {
          const parsed = JSON.parse(nextInputValue);
          setError(null);
          onChange(parsed);
        } catch (nextError) {
          setError(
            nextError instanceof Error ? nextError.message : "Invalid JSON"
          );
        }
      }}
      sx={styles.jsonEditor}
    />
  );
}
